import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Bell, Mail } from "lucide-react";
import { logDebug, logInfo } from "@/lib/structured-log";
import {
  setNotificationPreferenceServer,
  getUserNotificationPreferencesServer,
  listNotificationChannelsServer,
} from "@/server/notifications";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import type { NotificationEventType } from "@/lib/api/generated";
import { extractApiErrorMessage } from "@/lib/api/error";

const SYSTEM_EMAIL_CHANNEL_ID = "system-email";

// 所有支持的通知事件类型（与后端枚举保持一致）
const NOTIFICATION_EVENTS: NotificationEventType[] = [
  "customer_reply_received",
  "daily_report",
  "weekly_report",
];

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/account/notification")({
  loader: async ({ context, parentMatchPromise }) => {
    // Get workspaceId from parent layout context
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "settings.account.notification",
      parentMatchPromise,
    );

    // Call Server Functions to load data
    const [preferences, channels] = await Promise.all([
      getUserNotificationPreferencesServer({ data: { workspaceId } }),
      listNotificationChannelsServer({ data: { workspaceId } }),
    ]);

    const missingDefaultEvents = NOTIFICATION_EVENTS.filter(
      (eventType) => preferences[eventType] === undefined,
    );

    if (missingDefaultEvents.length > 0) {
      await Promise.all(
        missingDefaultEvents.map((eventType) =>
          setNotificationPreferenceServer({
            data: {
              workspaceId,
              payload: {
                event_type: eventType,
                channel_ids: [SYSTEM_EMAIL_CHANNEL_ID],
                is_enabled: true,
              },
            },
          }),
        ),
      );

      for (const eventType of missingDefaultEvents) {
        preferences[eventType] = [SYSTEM_EMAIL_CHANNEL_ID];
      }
    }

    return {
      preferences,
      channels,
      workspaceId,
    };
  },
  component: NotificationPage,
});

function NotificationPage() {
  const { t } = useTranslation(["settings", "common"]);
  const router = useRouter();
  // Get workspaceId and data from loader
  const { preferences: initialPreferences, channels, workspaceId } = Route.useLoaderData();

  // 初始化默认偏好：只在某个事件完全没有偏好记录时才默认选中所有可用渠道
  // 如果偏好记录存在但为空数组，说明用户主动取消了所有选择，应保持为空
  const initializeDefaultPreferences = (
    prefs: Record<string, string[]>,
    activeChannelIds: string[],
  ): Record<string, string[]> => {
    const initialized = { ...prefs };
    for (const event of NOTIFICATION_EVENTS) {
      // 只在事件完全没有偏好记录时才默认选中所有可用渠道
      // 如果 initialized[event] 不存在（undefined），说明没有偏好记录，默认选中
      // 如果 initialized[event] 是空数组，说明用户主动取消，保持空数组
      if (initialized[event] === undefined) {
        initialized[event] = activeChannelIds;
      }
    }
    return initialized;
  };

  const activeChannelIds: string[] = [];

  logDebug("settings.notification.preferences.loaded", {
    workspaceId,
    activeChannelIds,
    channelCount: channels.length,
    preferences: initialPreferences,
    // 详细记录每个渠道的信息
    channelsDetail: channels.map((ch) => ({
      id: ch.id,
      name: ch.name,
      type: ch.channel_type,
      isActive: ch.is_active,
    })),
    // 详细记录每个事件的偏好
    preferencesDetail: Object.entries(initialPreferences).map(([event, channelIds]) => ({
      event,
      channelIds,
      count: channelIds?.length || 0,
    })),
  });

  // 本地状态：event_type -> selected channel IDs
  const [preferences, setPreferences] = useState<Record<string, string[]>>(
    initializeDefaultPreferences(initialPreferences, activeChannelIds),
  );
  const [saving, setSaving] = useState<string | null>(null); // 当前正在保存的事件类型

  // 判断某个事件是否开启了邮件通知
  const isEmailEnabled = (eventType: NotificationEventType): boolean => {
    const channelIds = preferences[eventType] || [];
    // 只要有任何渠道 ID (包括 system-email 或历史遗留的 channel ID)，都视为开启
    return channelIds.length > 0;
  };

  // 切换邮件通知开关
  const toggleEmail = async (eventType: NotificationEventType) => {
    const isEnabled = isEmailEnabled(eventType);
    const currentChannels = preferences[eventType] || [];

    // 如果当前是开启状态 -> 关闭（清空渠道列表）
    // 如果当前是关闭状态 -> 开启（使用系统默认邮件渠道）
    // 注意：我们保留现有的 channelIds 逻辑，但新增时只使用 system-email
    const newChannels = isEnabled ? [] : [SYSTEM_EMAIL_CHANNEL_ID];

    setPreferences((prev) => ({
      ...prev,
      [eventType]: newChannels,
    }));

    // 保存到后端
    try {
      setSaving(eventType);

      logInfo("settings.notification.preference.save.started", {
        workspaceId,
        eventType,
        channelIds: newChannels,
        isEnabled: newChannels.length > 0,
      });

      await setNotificationPreferenceServer({
        data: {
          workspaceId,
          payload: {
            event_type: eventType,
            channel_ids: newChannels,
            is_enabled: newChannels.length > 0,
          },
        },
      });

      logInfo("settings.notification.preference.save.succeeded", {
        workspaceId,
        eventType,
      });
      toast.success(t("settings:account.notificationPage.saveSuccess"));
      router.invalidate();
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        t("settings:account.notificationPage.saveFailed"),
      );
      toast.error(message);
      // 回滚本地状态
      setPreferences((prev) => ({
        ...prev,
        [eventType]: currentChannels,
      }));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6">
      <div className="w-full">
        <div className="mb-6">
          <h2 className="text-[16px] font-semibold text-gray-900">
            {t("settings:account.notificationPage.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("settings:account.notificationPage.description")}
          </p>
        </div>

        <div className="w-full bg-white rounded-xl border border-[#DFDFDF] overflow-hidden">
          {/* 表头 */}
          <div className="flex px-6 h-[40px] items-center border-b border-[#DFDFDF] bg-[#f6f7f8]">
            <div className="flex-1 font-medium text-sm text-gray-900 flex items-center gap-2">
              <Bell className="size-4 text-gray-700" />
              {t("settings:account.notificationPage.notifyEvent")}
            </div>
            <div className="flex gap-2 flex items-center w-24 justify-center">
              <Mail className="size-4" />
              Email
            </div>
          </div>

          {/* 事件列表 */}
          <div>
            {NOTIFICATION_EVENTS.map((eventType, index) => (
              <div key={eventType}>
                <div className="flex px-6 py-2 hover:bg-gray-50">
                  {/* 事件名称和描述 */}
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900">
                      {t(`settings:account.notificationPage.events.${eventType}.title`)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t(`settings:account.notificationPage.events.${eventType}.description`)}
                    </div>
                  </div>

                  {/* 渠道复选框 */}
                  <div className="flex gap-8">
                    <div className="w-24 flex items-center justify-center">
                      <Checkbox
                        id={`${eventType}-email`}
                        checked={isEmailEnabled(eventType)}
                        onCheckedChange={() => toggleEmail(eventType)}
                        disabled={saving === eventType}
                      />
                    </div>
                  </div>
                </div>
                {index < NOTIFICATION_EVENTS.length - 1 && <Separator className="bg-[#DFDFDF]" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
